/**
 * Micro-interaction timings for the chat workspace and the dashboard shell.
 *
 * Source of truth is the design-approved reference package
 * (Zom-Chat-Review-for-Josh-2026-09-14/designs: zom-ui.css,
 * connected-card-review.css, workspace-chat.css, workspace-motion.css).
 * Controls settle colour, border and shadow in 160ms ease-out and press with
 * scale(.975) (rows: translateY(1px)); cards and chips move border and shadow
 * in 150ms; the composer ring settles in 200ms and the send button presses in
 * 220ms on cubic-bezier(.22,1,.36,1). Every string carries its own
 * reduced-motion opt-out so no call site needs a media query.
 *
 * Compose with cn() exactly like `focusRingTight` (see focus-ring.ts).
 */
import type { Transition } from "framer-motion";

/**
 * The CSS `ease-out` keyword, cubic-bezier(0,0,.58,1), which is the curve the
 * reference uses. Tailwind's `ease-out` token is the sharper Material curve
 * cubic-bezier(0,0,.2,1) and reads snappier at 160ms, so it is spelled out.
 */
const EASE_OUT = "ease-[cubic-bezier(0,0,0.58,1)]";

/** Reference settle curve for arrivals, presses and disclosure chevrons. */
const EASE_SETTLE = "ease-[cubic-bezier(0.22,1,0.36,1)]";

/** Buttons, links, tabs, icon buttons, menu items and list rows. */
export const controlMotion =
  "transition-[background-color,border-color,color,box-shadow,opacity,transform] " +
  `duration-[160ms] ${EASE_OUT} motion-reduce:transition-none`;

/** Press feedback for buttons, chips and tiles. */
export const controlPress =
  "active:scale-[0.975] motion-reduce:active:scale-100";

/** Press feedback for nav rows and full-width disclosure rows. */
export const rowPress =
  "active:translate-y-px motion-reduce:active:translate-y-0";

/** Cards that act as controls (starter cards, banners): border + shadow 150ms. */
export const cardHoverMotion =
  "transition-[border-color,box-shadow,background-color,color] " +
  `duration-150 ${EASE_OUT} motion-reduce:transition-none`;

/** Suggestion, NEXT and canvas chips: 150ms, including the active translate. */
export const chipMotion =
  "transition-[background-color,border-color,color,box-shadow,transform] " +
  `duration-150 ${EASE_OUT} motion-reduce:transition-none`;

/** Disclosure chevrons: 160ms on the settle curve (zom-ui.css .profile-chevron). */
export const chevronMotion = `transition-transform duration-[160ms] ${EASE_SETTLE} motion-reduce:transition-none`;

/** Composer box: focus-within border + soft ring (workspace-chat.css). */
export const composerFocusMotion = `transition-[border-color,box-shadow] duration-200 ${EASE_SETTLE} motion-reduce:transition-none`;

/** Composer send button: colour + press scale(.95) in 220ms. */
export const sendMotion =
  "transition-[background-color,border-color,color,transform] " +
  `duration-[220ms] ${EASE_SETTLE} active:scale-95 ` +
  "motion-reduce:transition-none motion-reduce:active:scale-100";

/** Sidebar controls inherit the same semantic feedback as other controls. */
export const sidebarRowMotion = controlMotion;

export const sidebarChevronMotion = chevronMotion;

export const sidebarFadeMotion = `transition-opacity duration-[160ms] ${EASE_OUT} motion-reduce:transition-none`;

/** The reference rail reveals its disclosure glyph without layout bounce. */
export const sidebarRevealMotion = `transition-[width] duration-[160ms] ${EASE_SETTLE} motion-reduce:transition-none`;

/** Named 120ms reference exception for Reports and Settings controls. */
export const workspaceControlMotion =
  "transition-[color,background-color,border-color,transform] duration-[120ms] ease-out motion-reduce:transition-none";

/* ------------------------------------------------------------------------- */
/*  Framer Motion equivalents for JS-driven pieces                           */
/* ------------------------------------------------------------------------- */

export const SETTLE_EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

/** Chevron and glyph rotation. */
export const CHEVRON_TRANSITION: Transition = {
  duration: 0.16,
  ease: SETTLE_EASE,
};

/** Card, panel and disclosure-body arrival (connected-card-review.css). */
export const PANEL_TRANSITION: Transition = {
  duration: 0.24,
  ease: SETTLE_EASE,
};

/** Route or view change: 180ms in (zom-ui.css). */
export const VIEW_ENTER_TRANSITION: Transition = {
  duration: 0.18,
  ease: SETTLE_EASE,
};

/** Calm working pulse period in seconds for dots and glyph rings. Never faster. */
export const CALM_PULSE_SECONDS = 1.8;
